import { useState, useEffect, useCallback } from 'react'
import api from '@/lib/axios'
import type { MediaItem } from '@/types/admin'

interface UseMediaPickerDialogVMArgs {
  open: boolean
  onSelect: (url: string, blobKey: string) => void
  onClose: () => void
}

export function useMediaPickerDialogVM({ open, onSelect, onClose }: UseMediaPickerDialogVMArgs) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)

  const fetchMedia = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get<{ media: MediaItem[] }>('/admin/media.json')
      setMediaItems(data.media.filter((m) => m.is_image))
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      fetchMedia()
      setSelectedId(null)
      setSearch('')
    }
  }, [open, fetchMedia])

  const filteredMedia = mediaItems.filter((m) =>
    m.filename.toLowerCase().includes(search.toLowerCase()) ||
    m.name.toLowerCase().includes(search.toLowerCase())
  )

  const selectedItem = mediaItems.find((m) => m.id === selectedId) ?? null

  const handleConfirm = () => {
    if (selectedItem) {
      onSelect(selectedItem.url, selectedItem.key)
      onClose()
    }
  }

  const handleUpload = async (file: File) => {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post<{ success: boolean; media: MediaItem }>('/admin/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (data.success) {
        setMediaItems((prev) => [data.media, ...prev])
        setSelectedId(data.media.id)
      }
    } catch {
      // silently fail
    } finally {
      setUploading(false)
    }
  }

  return {
    mediaItems: filteredMedia,
    loading,
    search,
    setSearch,
    selectedId,
    setSelectedId,
    selectedItem,
    uploading,
    handleConfirm,
    handleUpload,
  }
}
